import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Button from '../Button';
import { buildTutorials } from './tutorialSteps';
import { isTutorialComplete, readProgress, readStoredUserEmail } from './tutorialProgress';
import { requestTutorial } from '../../actions/tutorial';
import { tutorialAvailabilityContext } from './tutorialAvailability';
import captureTutorialAnalytics from './tutorialAnalytics';
import './tutorial.scss';

/**
 * The "Interactive Tutorials" page inside the Medsender Guide.
 *
 * The Guide is the app's existing help/reference area (containers/MedsenderGuide
 * .jsx), so the guided tutorials get a first-class entry there alongside the
 * written guides: users browse the full list, see what they've completed, and
 * launch any of them.
 *
 * Launching is a *request* (see actions/tutorial#requestTutorial) rather than a
 * direct start, because a tutorial has to run on its own screen — the app-wide
 * LearnMedsender container owns the router and the target-waiting logic and
 * picks the request up from here.
 */
export const GuideTutorialsBase = ({
  userEmail,
  hasEmrIntegration,
  impersonating,
  onRequestTutorial,
}) => {
  const progress = readProgress(userEmail);
  const tutorials = buildTutorials({ hasEmrIntegration, ...tutorialAvailabilityContext() }).map((tutorial) => ({
    ...tutorial,
    completed: isTutorialComplete(progress, tutorial.id),
  }));
  const completedCount = tutorials.filter((tutorial) => tutorial.completed).length;

  const launch = (tutorialId) => {
    captureTutorialAnalytics(impersonating, 'tutorial_launched_from_guide', { tutorial: tutorialId });
    onRequestTutorial(tutorialId);
  };

  return (
    <div className="guide-tutorials" data-tutorial-guide-section>
      <h1 className="guide-tutorials__title">Interactive Tutorials</h1>
      <p className="guide-tutorials__subtitle">
        Short, hands-on walkthroughs of the everyday Medsender workflows. Each one
        highlights the real buttons and lets you click through them yourself — you
        can stop at any point.
      </p>
      <p className="guide-tutorials__progress">
        {`${completedCount} of ${tutorials.length} complete`}
      </p>

      <div className="guide-tutorials__grid">
        {tutorials.map((tutorial, index) => (
          <div
            className={`guide-tutorials__card${tutorial.completed ? ' is-complete' : ''}`}
            key={tutorial.id}
          >
            <div className="guide-tutorials__card-head">
              <span className="guide-tutorials__badge" aria-hidden="true">
                {tutorial.completed ? '✓' : index + 1}
              </span>
              <h2 className="guide-tutorials__card-title">{tutorial.title}</h2>
            </div>
            <p className="guide-tutorials__card-body">{tutorial.description}</p>
            <p className="guide-tutorials__card-meta">
              {`${tutorial.steps.length} steps`}
              {tutorial.core ? '' : ' · Optional extra'}
              {tutorial.completed ? ' · Completed' : ''}
            </p>
            <Button
              type={tutorial.completed ? 'secondary' : 'primary'}
              size="xsmall"
              label={tutorial.completed ? 'Replay tutorial' : 'Start tutorial'}
              onClick={() => launch(tutorial.id)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

GuideTutorialsBase.defaultProps = {
  userEmail: '',
  hasEmrIntegration: false,
  impersonating: false,
};

GuideTutorialsBase.propTypes = {
  userEmail: PropTypes.string,
  hasEmrIntegration: PropTypes.bool,
  impersonating: PropTypes.bool,
  onRequestTutorial: PropTypes.func.isRequired,
};

export default connect((state) => ({
  // Same resolution order as LearnMedsender: `m8_uid` first, because it is the
  // one value logout clears and every login path rewrites. This previously read
  // the reducer first and omitted the stored-email fallback entirely, so it could
  // resolve a different namespace from the checklist it launches.
  userEmail: readStoredUserEmail() || state.userProfile.email || state.auth.data?.uid || '',
  hasEmrIntegration: !!state.auth.hasEmrIntegrationCredential || !!state.auth.data?.autoUploadEmr,
  impersonating: !!state.auth.impersonating,
}), {
  onRequestTutorial: requestTutorial,
})(GuideTutorialsBase);
